import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  Optional,
  Self,
  ViewChild,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldControl,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { UiAuthService } from '@fullyops/legacy/ui/index';
import { ZonedMomentDateAdapter } from '@fullyops/shared/zoned-moment-date-adapter';
import { MaterialModule } from '@fullyops/shared/material.module';
import { I18NextModule } from 'angular-i18next';
import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import {
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  startWith,
  Subscription,
} from 'rxjs';
import { TimeInputComponent } from '../time-input/time-input.component';
import {
  setLocalTimeMinutes,
  getLocalTimeMinutes,
} from '@fullyops/shared/local-time-minutes';

const selector = 'fo-date-time-input';

@Component({
  selector,
  standalone: true,
  imports: [
    MaterialModule,
    ReactiveFormsModule,
    I18NextModule,
    TimeInputComponent,
  ],
  templateUrl: './date-time-input.component.html',
  styleUrl: './date-time-input.component.scss',
  providers: [
    { provide: MatFormFieldControl, useExisting: DateTimeInputComponent },
    // MomentDateAdapter isn't providedIn: root, so we must inject it here so
    // that ZonedMomentDateAdapter can delegate to it.
    MomentDateAdapter,
    // Since we're not using MomentDateAdapter directly, we must specify the
    // date formats ourselves.
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: DateAdapter, useClass: ZonedMomentDateAdapter },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
  ],
})
export class DateTimeInputComponent
  implements
    OnDestroy,
    ControlValueAccessor,
    MatFormFieldControl<Moment | string>,
    OnInit
{
  @Input() mainLabel: string;
  @Input() mainLabelLiteral = false;
  @Input() timeLabel: string = 'forms:chooseTime';
  @Input() dateFilter?: (d: Moment | null) => boolean;
  @Input() timeAlwaysEnabled: boolean = false;
  @Input() minTimeMinutes = 0;
  @Input() maxTimeMinutes = 1440;

  // Keep the validation status of the date field synced with that of the form control that
  // this component as a whole is linked to.
  protected form = new FormGroup({
    date: new FormControl<Moment | null>(null, (_) => this.ngControl.errors),
    time: new FormControl<number | null>(null),
  });

  // implementation of ControlValueAccessor to enable use with reactive forms

  private sub = new Subscription();

  ngOnInit() {
    this.sub.add(this.form.valueChanges.subscribe((_) => this.onTouched()));
    // When the user clears the date field, clear the time field as well;
    // this more clearly reflects the change that is being made to the date-time field's value,
    // and is also necessary for that change to pass the filter applied in registerOnChange.
    this.sub.add(
      this.form.valueChanges
        .pipe(
          map((_) => this.form.getRawValue()),
          startWith(this.form.getRawValue()),
          // Avoid repeatedly triggering when the user is manually typing in a date,
          // or when fields other than the date are changed.
          distinctUntilChanged((a, b) => a.date === b.date),
        )
        .subscribe((v) => {
          if (v.date == null && v.time != null) {
            this.form.controls.time.setValue(null);
          }
        }),
    );
    this.sub.add(
      this.ngControl.statusChanges.subscribe(() => {
        this.form.controls.date.updateValueAndValidity({ emitEvent: false });
        this.form.controls.date.markAsTouched();
      }),
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  private onTouched = () => {};

  // Accept plain ISO strings as input for compatibility with legacy forms.
  writeValue(v: Moment | string) {
    if (v == null) {
      this.form.setValue({ date: null, time: null }, { emitEvent: false });
      return;
    }
    const t = (typeof v === 'string' ? moment(v) : v.clone()).tz(
      this.authService.currentUser.timeZone,
    );
    const midnight = t.clone().startOf('day');
    const time = getLocalTimeMinutes(t);
    this.form.setValue(
      {
        date: midnight,
        time,
      },
      { emitEvent: false },
    );
  }

  registerOnChange(f: (v: Moment) => void): void {
    this.sub.add(
      this.form.valueChanges
        .pipe(
          // If the time field is enabled but the date field isn't, we still need to read
          // the date field to produce the final value.
          map((_) => this.form.getRawValue()),
          // Don't report a change if we're in an intermediate state with only one field set
          // (can happen during user input)
          filter((v) => (v.date == null) === (v.time == null)),
          map((v) => {
            if (v.date == null || v.time == null) {
              return null;
            }
            return setLocalTimeMinutes(v.date, v.time);
          }),
        )
        .subscribe(f),
    );
  }

  registerOnTouched(f: () => void): void {
    this.onTouched = f;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.timeAlwaysEnabled) {
      if (isDisabled) {
        this.form.controls.date.disable();
      } else {
        this.form.controls.date.enable();
      }
    } else {
      if (isDisabled) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  // implementation of MatFormFieldControl

  @ViewChild('dateInput', { static: true, read: MatInput })
  private internalDateInput: MatInput;

  @ViewChild('timeInput', { static: true, read: MatInput })
  private internalTimeInput: MatInput;

  constructor(
    private authService: UiAuthService,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
  ) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
  }

  get value() {
    return this.ngControl.value;
  }

  set value(v: Moment | string) {
    this.writeValue(v);
  }

  get stateChanges(): Observable<void> {
    return merge(
      this.internalDateInput.stateChanges,
      this.internalTimeInput.stateChanges,
    ).pipe(map(() => undefined));
  }

  static nextId = 0;

  @HostBinding() id = selector + '-' + DateTimeInputComponent.nextId++;

  get placeholder() {
    return '';
  }

  get focused() {
    return this.internalDateInput.focused || this.internalTimeInput.focused;
  }

  get empty() {
    return this.internalDateInput.empty && this.internalTimeInput.empty;
  }

  get shouldLabelFloat() {
    return true;
  }

  get required() {
    return this.ngControl.control.hasValidator(Validators.required);
  }

  get disabled() {
    return this.ngControl.disabled;
  }

  get errorState() {
    return this.ngControl.touched && this.ngControl.errors !== null;
  }

  get controlType() {
    return selector;
  }

  setDescribedByIds(ids: string[]): void {
    this._elementRef.nativeElement.setAttribute(
      'aria-describedby',
      ids.join(' '),
    );
  }

  onContainerClick(_: MouseEvent) {}
}
